/**
 * A client's Ducks in a Row, from the Sidekick's side. Read only — there are no
 * mutations here on purpose: the list is the client's, and a duck the client
 * has put in their tracker is worked on as an ordinary task on their board.
 *
 * 404 means the client doesn't have Ducks in a Row switched on; 403 means this
 * Sidekick isn't (or is no longer) on the client's team.
 */
import { useQueries, useQuery } from "@tanstack/react-query";

import { useAuth } from "@/hooks/use-auth";
import { apiRequest } from "@/lib/queryClient";
import type { DucksDashboard } from "@shared/ducks";

export const clientDucksKey = (clientUid: string) => ["client-ducks", clientUid] as const;

const clientDucksQuery = (clientUid: string) => ({
  queryKey: clientDucksKey(clientUid),
  // The client ticks things off on their side; keep a revisit fresh rather
  // than serving the app-wide five minutes.
  staleTime: 30_000,
  queryFn: async () =>
    (await (await apiRequest("GET", `/sidekick/ducks/clients/${clientUid}`)).json()) as DucksDashboard,
});

export function useClientDucks(clientUid: string | undefined) {
  return useQuery<DucksDashboard>({
    ...clientDucksQuery(clientUid ?? ""),
    enabled: !!clientUid,
  });
}

/** One dashboard per client, sharing the cache with the client's own page. */
export function useClientsDucks(clientUids: string[]) {
  return useQueries({ queries: clientUids.map(clientDucksQuery) });
}

export type DucksClient = { client_uid: string; client_name: string };

/**
 * This Sidekick's clients with Ducks in a Row switched on. The nav mounts this on
 * every page, so it stays on the app-wide five minutes.
 *
 * Its own key, not the roster's ["/client_summary/pa/clients", uid]: every page
 * sharing that key stores a different shape under it (clients.tsx adds an `id`
 * and builds its links from it), and the nav loads first, so its shape would win.
 */
export function useDucksClients() {
  const { user } = useAuth();
  return useQuery<DucksClient[]>({
    queryKey: ["ducks-clients", user?.uid],
    enabled: !!user?.uid,
    queryFn: async () => {
      const res = await apiRequest("GET", `/client_summary/pa/${user?.uid}/clients`);
      const { clients } = (await res.json()) as {
        clients?: (DucksClient & { ducks_enabled?: boolean })[];
      };
      return (clients ?? []).filter((c) => c.ducks_enabled);
    },
  });
}
